import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  IconButton,
  useColorModeValue,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Text,
  HStack,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Button,
  Badge,
  Tooltip,
} from "@chakra-ui/react";
import {
  FiMenu,
  FiUser,
  FiSettings,
  FiLogOut,
  FiBell,
  FiRefreshCw,
} from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import AdminSidebar from "./AdminSidebar";
import AdminDashboardOverview from "./AdminDashboardOverview";
import CategoryManagement from "./CategoryManagement";
import PenaltyManagement from "./PenaltyManagement";
import EstateManagement from "./EstateManagement";
import DivisiManagement from "./DivisiManagement";
import BlokManagement from "./BlokManagement";
import JobLevelManagement from "./JobLevelManagement";
import ForemanGroupManagement from "./ForemanGroupManagement";
import MasterSyncManagement from "./MasterSyncManagement";
import BlokInspeksiManagement from "./BlokInspeksiManagement";
import LoginRequestManagement from "./LoginRequestManagement";
import { ActivityLogsDashboard, AuditLogsManagement } from "../audit_logs";
import { ADMIN_THEME_CONFIG } from "../config";
import CategoryPermissionsPage from "../../pages/CategoryPermissionsPage";
import CategoryPermissions from "../../components/user/CategoryPermissions";

interface AdminLayoutProps {
  children?: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeItem, setActiveItem] = useState(() => {
    return location.state?.activeItem || "dashboard";
  });

  useEffect(() => {
    if ((location.pathname === "/admin" || location.pathname === "/admin/") && location.state?.activeItem) {
      setActiveItem(location.state.activeItem);
    }
  }, [location.pathname, location.state]);

  const { user, logout } = useAuth();

  // Color mode values
  const bg = useColorModeValue("gray.50", "gray.900");
  const headerBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "gray.100");

  const isAdminRoot = location.pathname === "/admin" || location.pathname === "/admin/";

  // Handle navigation
  const handleNavigation = (item: string) => {
    if (!isAdminRoot) {
      navigate("/admin", { state: { activeItem: item } });
    } else {
      setActiveItem(item);
    }
    if (isOpen) onClose(); // Close mobile drawer
  };

  // Handle logout
  const handleLogout = () => {
    logout();
  };

  // Render content based on active item
  const renderContent = () => {
    if (children) {
      return children;
    }

    switch (activeItem) {
      case "dashboard":
        return <AdminDashboardOverview />;
      case "inspections":
        return <BlokInspeksiManagement />;
      case "categories":
        return <CategoryManagement />;
      case "penalties":
        return <PenaltyManagement />;
      case "estates":
        return <EstateManagement />;
      case "divisis":
        return <DivisiManagement />;
      case "bloks":
        return <BlokManagement />;
      case "job-levels":
        return <JobLevelManagement />;
      case "foreman-groups":
        return <ForemanGroupManagement />;
      case "master-sync":
        return <MasterSyncManagement />;
      case "permissions":
        return <CategoryPermissions />;
      case "login-requests":
        return <LoginRequestManagement />;
      case "audit-logs":
        return <AuditLogsManagement />;
      case "activity-logs":
        return (
          <Box p={6}>
            <ActivityLogsDashboard />
          </Box>
        );
      case "employees":
        return (
          <Box p={6}>
            <Text fontSize="xl" fontWeight="bold" mb={4}>
              Manajemen Karyawan
            </Text>
            <Text color="gray.500">
              Halaman manajemen karyawan sedang dalam pengembangan...
            </Text>
          </Box>
        );
      case "settings":
        return (
          <Box p={6}>
            <Text fontSize="xl" fontWeight="bold" mb={4}>
              Pengaturan Sistem
            </Text>
            <Text color="gray.500">
              Halaman pengaturan sistem sedang dalam pengembangan...
            </Text>
          </Box>
        );
      default:
        return <AdminDashboardOverview />;
    }
  };

  return (
    <Box minH="100vh" bg={bg}>
      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Text color={ADMIN_THEME_CONFIG.COLORS.PRIMARY} fontWeight="bold">
              Admin Panel
            </Text>
          </DrawerHeader>
          <DrawerBody p={0}>
            <AdminSidebar
              activeItem={isAdminRoot ? activeItem : ""}
              onItemClick={handleNavigation}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Flex h="100vh">
        {/* Desktop Sidebar */}
        <Box display={{ base: "none", lg: "block" }}>
          <AdminSidebar
            activeItem={isAdminRoot ? activeItem : ""}
            onItemClick={handleNavigation}
          />
        </Box>

        {/* Main Content */}
        <Flex direction="column" flex="1" overflow="hidden">
          {/* Top Header */}
          <Box
            bg={headerBg}
            borderBottom="1px solid"
            borderColor={borderColor}
            px={6}
            py={4}
            position="sticky"
            top={0}
            zIndex={10}
          >
            <Flex justify="space-between" align="center">
              {/* Left side - Mobile menu + Title */}
              <HStack spacing={4}>
                <IconButton
                  aria-label="Open menu"
                  icon={<FiMenu />}
                  variant="ghost"
                  display={{ base: "flex", lg: "none" }}
                  onClick={onOpen}
                />
                <Text
                  fontSize="xl"
                  fontWeight="semibold"
                  color={textColor}
                  display={{ base: "none", md: "block" }}
                >
                  {(() => {
                    if (location.pathname.startsWith("/admin/applications")) return "Manajemen Aplikasi";
                    if (location.pathname.startsWith("/admin/menus")) return "Manajemen Menu";
                    if (location.pathname.startsWith("/admin/roles")) return "Manajemen Role";
                    if (location.pathname.startsWith("/admin/users")) return "Manajemen User RBAC";

                    switch (activeItem) {
                      case "dashboard": return "Dashboard Admin";
                      case "inspections": return "Manajemen Sinkronisasi Inspeksi";
                      case "users": return "Manajemen Pengguna";
                      case "categories": return "Manajemen Kategori";
                      case "employees": return "Manajemen Karyawan";
                      case "estates": return "Manajemen Estate & Divisi";
                      case "bloks": return "Manajemen Blok";
                      case "job-levels": return "Manajemen Job Level";
                      case "foreman-groups": return "Manajemen Foreman Groups";
                      case "master-sync": return "Sinkronisasi Data Master";
                      case "login-requests": return "Manajemen Login Request";
                      case "audit-logs": return "Audit Log Monitoring";
                      case "activity-logs": return "Aktivitas Menu";
                      case "permissions": return "Izin Kategori Pengguna";
                      case "settings": return "Pengaturan Sistem";
                      default: return "Dashboard Admin";
                    }
                  })()}
                </Text>
              </HStack>

              {/* Right side - Actions + User menu */}
              <HStack spacing={4}>
                {/* Refresh Button */}
                <Tooltip label="Refresh Data">
                  <IconButton
                    aria-label="Refresh"
                    icon={<FiRefreshCw />}
                    variant="ghost"
                    size="sm"
                    onClick={() => window.location.reload()}
                  />
                </Tooltip>

                {/* Notifications */}
                <Box position="relative">
                  <IconButton
                    aria-label="Notifications"
                    icon={<FiBell />}
                    variant="ghost"
                    size="sm"
                  />
                  <Badge
                    position="absolute"
                    top="-1"
                    right="-1"
                    colorScheme="red"
                    borderRadius="full"
                    fontSize="xs"
                    minW="18px"
                    h="18px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    3
                  </Badge>
                </Box>

                {/* User Menu */}
                <Menu>
                  <MenuButton as={Button} variant="ghost" size="sm">
                    <HStack spacing={2}>
                      <Avatar name={user?.email || "Admin"} size="sm" />
                      <Text
                        fontSize="sm"
                        display={{ base: "none", md: "block" }}
                        color={textColor}
                      >
                        {user?.email || "admin@example.com"}
                      </Text>
                    </HStack>
                  </MenuButton>
                  <MenuList>
                    <MenuItem icon={<FiUser />}>
                      Profil Saya
                    </MenuItem>
                    <MenuItem icon={<FiSettings />}>
                      Pengaturan
                    </MenuItem>
                    <MenuDivider />
                    <MenuItem
                      icon={<FiLogOut />}
                      onClick={handleLogout}
                      color="red.500"
                    >
                      Logout
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
            </Flex>
          </Box>

          {/* Content Area */}
          <Box flex="1" overflow="auto" bg={bg}>
            {renderContent()}
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
};

export default AdminLayout;
